import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector as EmployeeSelectorComponent } from 'modules/employees/components/EmployeeSelector'

import { BaseEntitySelectorWrapper } from '../BaseEntitySelectorWrapper'

interface Props {
  defaultOpen?: boolean
  defaultEmployees?: EmployeeForSelectFragmentNoAccess[]
  onChange: (value: EmployeeForSelectFragmentNoAccess[] | undefined) => void
}

const EmployeeSelector = ({ onChange, defaultEmployees, defaultOpen }: Props) => {
  const [employees, setEmployees] = useState<EmployeeForSelectFragmentNoAccess[]>([])
  const { canSeeAllCompanyData } = useOutletContext<{
    canSeeAllCompanyData: boolean
  }>()

  const handleValueChanged = useUnsafeCallback(
    (value: EmployeeForSelectFragmentNoAccess[]) => {
      setEmployees(value)
      onChange(value)
    },
    [onChange]
  )

  // set default value as current value if there is no current value
  useUnsafeEffect(() => {
    if (defaultEmployees?.length && !employees.length) {
      handleValueChanged(defaultEmployees)
    }
  }, [employees, defaultEmployees, handleValueChanged])

  const hasEmployeesSelected = employees.length > 0

  return (
    <EmployeeSelectorComponent
      multiple
      hideBorder
      onlyManagers
      displayNameStrategy="full"
      value={employees}
      onSelect={(selectedEmployees) => handleValueChanged(selectedEmployees)}
      defaultOpen={defaultOpen}
      applySearchToGroupIn={['managers']}
      {...(!canSeeAllCompanyData && { onlyMyReportees: true })}
    >
      <BaseEntitySelectorWrapper
        selected={hasEmployeesSelected}
        placeholder={i18n.t('common.person.other')}
        selectedTitle={i18n.t('common.person.other')}
        selectedCount={employees.length}
      />
    </EmployeeSelectorComponent>
  )
}

export default EmployeeSelector
